import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { postUser, deleteUser } from "../../helper/http";
import { useLanguage } from "../../context/LanguageContext";
import styles from "./TableDetailPage.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTable,
  faFont,
  faHashtag,
  faCheckSquare,
  faCalendar,
  faLink,
  faPaperclip,
  faCircle,
  faArrowLeft,
  faPlus,
  faEdit,
  faTrash,
  faSave,
  faTimes,
  faSortAmountDown,
  faListUl,
  faKey,
  faSortUp,
} from "@fortawesome/free-solid-svg-icons";
import { ToastService } from "../../service/toast.service";
import SearchableDropdown from "../../components/SearchableDropdown/SearchableDropdown";
import DeleteConfirmModal from "../../components/DynamicTable/Modals/DeleteConfirmModal/DeleteConfirmModal";
import { FIELD_TYPES } from "../../types/fieldTypes";

interface Field {
  id: number;
  name: string;
  file_type: string;
  module_id: number;
  relation_module?: string;
  relation_field?: string;
  order?: number;
  default_value?: any;
    has_index?: boolean;
}

interface Table {
  id: number;
  name: string;
  table_name: string;
  template_type?: string;
  fieldsCount?: number;
  fields?: Field[];
  sort_field_id?: number | null;
  list_field_id?: number | null;
  favorite?: number;
  is_system?: boolean;
}

interface Report {
  id: number;
  name: string;
  template_type?: string;
  fieldsCount?: number;
  fields?: Field[];
}

export default function TableDetailPage(props: {isReportMode?: boolean}) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [table, setTable] = useState<Table | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddField, setShowAddField] = useState(false);
  const [filter, setFilter] = useState('');
  const [relationFilter, setRelationFilter] = useState('');
  const [editingFieldId, setEditingFieldId] = useState<number | null>(null);
  const [editFieldData, setEditFieldData] = useState<{
    name: string;
    file_type: string;
    relation_module: string;
  }>({
    name: "",
    file_type: "string",
    relation_module: '',
  });
  const [newFieldData, setNewFieldData] = useState({
    name: "",
    file_type: "string",
    relation_module: '',
  });

  const isFavorite = useRef(null);
  const favorite = useRef(null);

  const [relOptions, setRelOptions] = useState([]);
  const [deletingFieldId, setDeletingFieldId] = useState<number | null>(null);
  const [deletingFieldName, setDeletingFieldName] = useState<string>("");
  
  const cardBodyRef = useRef<HTMLDivElement>(null);
  const previousScrollRef = useRef(0);

  useEffect(() => {
    if (id) {
      loadTable();
    }
  }, [id]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "=" || e.key === "+")) {
        e.preventDefault();
        setShowAddField(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    if (showAddField && cardBodyRef.current) {
      setTimeout(() => {
        cardBodyRef.current?.scrollTo({
          top: cardBodyRef.current.scrollHeight,
          behavior: "smooth",
        });
      }, 100);
    }
  }, [showAddField]);

  const loadTable = async () => {
    try {
      setLoading(true);

      if (!props.isReportMode) {
        const tablesRes = await postUser("crud/list", {
          module: "module",
          page: "1",
          per_page: "100",
          sort_direction: "+",
          sort_field: "order",
        });

        const foundTable = tablesRes.data.find((t: Table) => String(t.id) === String(id));

        if (foundTable) {
          const fieldsRes = await postUser("crud/list", {
            module: "module_field",
            page: "1",
            per_page: "100",
            sort_direction: "+",
            sort_field: "order",
            filters: {module_id: +foundTable.id},
          });


          setTimeout(() => {
              if (favorite.current) {
                  (favorite.current as HTMLInputElement).value = foundTable.favorite || 0;
              }

              if (isFavorite.current) {
                  (isFavorite.current as HTMLInputElement).checked = (foundTable.favorite || 0) > 0;
              }
          }, 100);



          setTable({
            ...foundTable,
            fieldsCount: fieldsRes.total || 0,
            fields: fieldsRes.data || [],
          });
        } else {
          console.error("Table not found with ID:", id);
        }
      } else {
        const reportRes = await postUser("crud/list", {
          module: "report",
          page: "1",
          per_page: "100",
          sort_direction: "+",
          sort_field: "id",
        });
        const foundReport = reportRes.data.find((t: Table) => String(t.id) === String(id));

        const fields = JSON.parse(foundReport.table_fields);

        setTable({
          ...foundReport,
          fieldsCount: fields.length|| 0,
          fields: fields || [],
        });
      }
    } catch (error: any) {
      console.log(error);
      ToastService.error(t("error") || "Error loading table");
    } finally {
      setLoading(false);
    }
  };

  const handleSetIndexField = async (fieldId: number, has_index: boolean) => {
      if (!table) return;


      await postUser("crud/upsert", {
          module: "module_field",
          body: {
              id: fieldId,
              module_id: Number(table.id),
              has_index,
          },
      });

      await loadTable();
  }

  const handleSetSortField = async (fieldId: number) => {
    if (!table) return;
    
    try {
      const newSortFieldId = table.sort_field_id === fieldId ? null : fieldId;
      
      await postUser("crud/upsert", {
        id: table.id,
        module: "module",
        body: {
          name: table.name,
          table_name: table.table_name,
          template_type: table.template_type,
          sort_field_id: newSortFieldId,
          list_field_id: table.list_field_id, 
        },
      });

      setTable({
        ...table,
        sort_field_id: newSortFieldId,
      });

      ToastService.success(
        newSortFieldId 
          ? t("sortFieldSet")
          : t("sortFieldUnset")
      );
    } catch (error: any) {
      console.error(error);
      ToastService.error("Error setting sort field");
    }
  };

  const handleSetListField = async (fieldId: number) => {
    if (!table) return;
    
    try {
      const newListFieldId = table.list_field_id === fieldId ? null : fieldId;
      
      await postUser("crud/upsert", {
        id: table.id,
        module: "module",
        body: {
          name: table.name,
          table_name: table.table_name,
          template_type: table.template_type,
          sort_field_id: table.sort_field_id, 
          list_field_id: newListFieldId,
        },
      });

      setTable({
        ...table,
        list_field_id: newListFieldId,
      });

      ToastService.success(
        newListFieldId 
          ? t("listFieldSet")
          : t("listFieldUnset")
      );
    } catch (error: any) {
      console.error(error);
      ToastService.error("Error setting list field");
    }
  };

  const validateFieldName = (name: string): boolean => {
    const cyrillicPattern = /[а-яА-ЯёЁ]/;
    return !cyrillicPattern.test(name);
  };

  const handleAddField = async () => {
    if (!table || !newFieldData.name.trim()) return;

    if (!validateFieldName(newFieldData.name)) {
      ToastService.error(t("fieldNameLatinOnly") || "Field name must contain only Latin characters");
      return;
    }

    try {
      const maxOrder =
        table.fields && table.fields.length > 0
          ? Math.max(...table.fields.map((f: any) => f.order || 0))
          : 0;

      const needsRelation = newFieldData.file_type === 'fk';

      await postUser("crud/upsert", {
        module: "module_field",
        body: {
          module_id: Number(table.id),
          name: newFieldData.name.trim(),
          file_type: newFieldData.file_type,
          order: maxOrder + 1,
          relation_module: needsRelation ? newFieldData.relation_module : null,
          relation_field: needsRelation ? 'id' : null,
          default_value: null,
        },
      });

      if (cardBodyRef.current) {
        previousScrollRef.current = cardBodyRef.current.scrollTop;
      }

      await loadTable();

      setTimeout(() => {
        if (cardBodyRef.current) {
          cardBodyRef.current.scrollTop = previousScrollRef.current;
        }
      }, 0);

      setShowAddField(false);
      setNewFieldData({ name: "", file_type: "string", relation_module: '' });

      ToastService.success(t("fieldAdded"));

    } catch (error: any) {
      ToastService.error("Error adding field");
    }
  };

  const handleEditField = (field: Field) => {
    setEditingFieldId(field.id);
    setEditFieldData({
      name: field.name,
      file_type: field.file_type,
      relation_module: field.relation_module || '',
    });
    
    if (field.file_type === 'fk') {
      handleRelationDropdownOpen();
    }
  };

  const handleOrderChangeCheck = async () => {
      const favoriteValue = ( (isFavorite.current as HTMLInputElement | null)?.checked || false);
      if (favorite.current && !favoriteValue) {
          (favorite.current as HTMLInputElement).value = '0';
      }
      await handleOrderChange();
  }

   const handleOrderChange = async () => {

       if (!table) return;

       const favoriteValue = +( (favorite.current as HTMLInputElement | null)?.value || 0);

       if (isFavorite.current) {
           (isFavorite.current as HTMLInputElement).checked = favoriteValue > 0;
       }


       if (favoriteValue != table.favorite) {

           table.favorite = favoriteValue;
           await postUser("crud/upsert", {
               id: Number(table.id),
               module: "module",
               body: {
                   favorite: favoriteValue,
                   name: table.name,
                   table_name: table.table_name,
                   template_type: table.template_type,
               },
           });
       }
    }

  const handleSaveEdit = async (fieldId: number) => {
    if (!editFieldData.name.trim()) return;

    if (!validateFieldName(editFieldData.name)) {
      ToastService.error(t("fieldNameLatinOnly") || "Field name must contain only Latin characters");
      return;
    }

    try {
      const fieldToUpdate = table?.fields?.find(f => f.id === fieldId);
      if (!fieldToUpdate) return;

      const needsRelation = editFieldData.file_type === 'fk';

      await postUser("crud/upsert", {
        id: fieldId,
        module: "module_field",
        body: {
          module_id: Number(table?.id),
          name: editFieldData.name.trim(),
          file_type: editFieldData.file_type,
          order: fieldToUpdate.order,
          relation_module: needsRelation ? editFieldData.relation_module : null,
          relation_field: needsRelation ? 'id' : null,
          default_value: fieldToUpdate.default_value,
        },
      });

      if (cardBodyRef.current) {
        previousScrollRef.current = cardBodyRef.current.scrollTop;
      }

      await loadTable();

      setTimeout(() => {
        if (cardBodyRef.current) {
          cardBodyRef.current.scrollTop = previousScrollRef.current;
        }
      }, 0);

      setEditingFieldId(null);
      setEditFieldData({ name: "", file_type: "string", relation_module: '' });

      ToastService.success(t("fieldUpdated"));

    } catch (error: any) {
      ToastService.error("Error updating field");
    }
  };

  const handleCancelEdit = () => {
    setEditingFieldId(null);
    setEditFieldData({ name: "", file_type: "string", relation_module: '' });
  };

  const handleDeleteField = async (field: Field) => {
    setDeletingFieldId(field.id);
    setDeletingFieldName(field.name);
  };

  const confirmDelete = async () => {
    if (!deletingFieldId) return;

    try {
      await deleteUser("crud/delete", {
        id: deletingFieldId,
        module: "module_field",
      });

      if (cardBodyRef.current) {
        previousScrollRef.current = cardBodyRef.current.scrollTop;
      }

      await loadTable();

      setTimeout(() => {
        if (cardBodyRef.current) {
          cardBodyRef.current.scrollTop = previousScrollRef.current;
        }
      }, 0);

      ToastService.success(t("fieldDeleted"));

    } catch (error: any) {
      ToastService.error("Error deleting field");
    } finally {
      setDeletingFieldId(null);
      setDeletingFieldName("");
    }
  };

  const cancelDelete = () => {
    setDeletingFieldId(null);
    setDeletingFieldName("");
  };

  const getFieldTypeIcon = (fileType: string) => {
    switch (fileType) {
      case "id":
      case "string_disabled":
        return faKey;
      case "string":
      case "password":
        return faFont;
      case "text":
        return faFont; 
      case "number":
      case "integer":
      case "int":
      case "double":
      case "bigint":
        return faHashtag;
      case "boolean":
        return faCheckSquare;
      case "date":
      case "datetime":
      case "timestamp":
      case "time":
        return faCalendar;
      case "fk":
      case "multiselect":
        return faLink;
      case "file":
        return faPaperclip;
      case "color":
        return faCircle; 
      case "json":
        return faCircle; 
      default:
        return faCircle;
    }
  };

  const handleDropdownOpen = () => {
    setFilter('');

    if (showAddField && cardBodyRef.current) {
      setTimeout(() => {
        cardBodyRef.current?.scrollTo({
          top: cardBodyRef.current.scrollHeight,
          behavior: "smooth",
        });
      }, 100);
    }
  };

  const handleRelationDropdownOpen = () => {
    setRelationFilter('');
    postUser('crud/list', {
      "module": "module",
      "page": "1",
      "per_page": "100",
      "sort_direction": "+",
      "sort_field": "order"
    }).then(res => setRelOptions(res.data.map((item: { name: any; id: any; }) => ({label: item.name, value: item.id}))));
    
    if (showAddField && cardBodyRef.current) {
      setTimeout(() => {
        cardBodyRef.current?.scrollTo({
          top: cardBodyRef.current.scrollHeight,
          behavior: "smooth",
        });
      }, 100);
    }
  };

  const getFieldTypeLabel = (fileType: string): string => {
    if (fileType === 'string_disabled') {
      return t('idLabel');
    }
    
    const fieldTypeMeta = FIELD_TYPES.find(ft => ft.value === fileType);
    return fieldTypeMeta ? t(fieldTypeMeta.labelKey) : fileType;
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>{t("loading")}</div>
      </div>
    );
  }

  if (!table) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>{t("tableNotFound")}</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <button onClick={() => navigate("/tables")} className={styles.backButton}>
        <FontAwesomeIcon icon={faArrowLeft} />
      </button>

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.cardIcon}>
            <FontAwesomeIcon icon={faTable} size="4x" />
          </div>

          <div className={styles.cardInfo}>
            <h1 className={styles.cardTitle}>{table.name}</h1>
            <p className={styles.cardDbName}>{table.table_name}</p>
          </div>
        </div>
          <div className={styles.cardHeader}>
              Выводить в меню:
              <input type="checkbox" ref={isFavorite} onChange={handleOrderChangeCheck}/>
              Порядок:
              <input type="number" ref={favorite} onChange={handleOrderChange} onKeyDown={handleOrderChange}/>
          </div>

       <div className={styles.cardBody} ref={cardBodyRef}>
          {table.fields && table.fields.length > 0 ? (
            <div className={styles.fieldsList}>
              {table.fields.map((field) => (
                <div key={field.id} className={styles.fieldItem}>
                  {editingFieldId === field.id ? (
                    <div className={styles.editFieldForm}>
                      <input
                        type="text"
                        placeholder={t("fieldName")}
                        value={editFieldData.name}
                        onChange={(e) => {
                          const value = e.target.value;
                          setEditFieldData({ ...editFieldData, name: value });  
                        }}
                        className={`${styles.fieldInput} ${
                          editFieldData.name && !validateFieldName(editFieldData.name) 
                            ? styles.fieldInputError 
                            : ''
                        }`}
                        autoFocus
                      />
                      <SearchableDropdown
                        label={t("fieldType")}
                        onOpen={handleDropdownOpen}
                        options={FIELD_TYPES.map(ft => ({
                          label: t(ft.labelKey),
                          value: ft.value,
                        })).filter(item =>
                          item.value?.toLowerCase().includes(filter.toLowerCase()) ||
                          item.label?.toLowerCase().includes(filter.toLowerCase())
                        )}
                        value={editFieldData.file_type}
                        onSearch={(query: string) => setFilter(query)}
                        onChange={(value) => {
                          setEditFieldData({ ...editFieldData, file_type: value });
                          if (value === "fk") handleRelationDropdownOpen();
                        }}
                      />

                      {(() => {
                        const meta = FIELD_TYPES.find(ft => ft.value === editFieldData.file_type);
                        if (!meta) return null;

                        return (
                          <div className={styles.fieldTypeInfo}>
                            <div className={styles.fieldTypeBLock}>
                              <div className={styles.fieldTypeIcon}>
                                <FontAwesomeIcon icon={meta.icon} />
                              </div>

                              <div className={styles.fieldTypeDesc}>
                                {t(meta.descriptionKey)}
                              </div>
                            </div>

                            {meta.example && (
                              <div className={styles.fieldTypeExample}>
                                <span className={styles.exampleValue}>
                                  {t(meta.exampleKey)}
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {editFieldData.file_type == 'fk' && (
                        <SearchableDropdown
                          label={t("relationModule")}
                          onOpen={handleRelationDropdownOpen}
                          options={relOptions.filter((item: any) => {
                            return item.label.toString().toLowerCase().indexOf(relationFilter.toLowerCase()) >= 0;
                          })}
                          value={editFieldData.relation_module}
                          onChange={(value) =>
                            setEditFieldData({ ...editFieldData, relation_module: value })
                          }
                          onSearch={(query: string) => {
                            setRelationFilter(query);
                          }}
                        />
                      )}

                      <div className={styles.editFieldButtons}>
                        <button 
                          onClick={() => handleSaveEdit(field.id)} 
                          className={styles.saveFieldBtn}
                        >
                          <FontAwesomeIcon icon={faSave} />
                          {t("save")}
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className={styles.cancelFieldBtn}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                          {t("cancel")}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <span className={styles.fieldIcon}>
                        <FontAwesomeIcon icon={getFieldTypeIcon(field.file_type)} />
                      </span>
                      <span className={styles.fieldName}>{field.name}</span>
                      <span className={styles.fieldType}>{getFieldTypeLabel(field.file_type)}</span>
                      
                      {!props.isReportMode && (
                        <div className={styles.fieldMarkers}>
                          <button
                            onClick={() => handleSetIndexField(field.id, !field.has_index )}
                            className={`${styles.markerBtn} ${
                              field.has_index ? styles.markerActive : ''
                            }`}
                            title={t("setIndexField")}
                          >
                            <FontAwesomeIcon icon={faSortUp} />
                          </button>
                          <button
                            onClick={() => handleSetSortField(field.id)}
                            className={`${styles.markerBtn} ${
                              table.sort_field_id === field.id ? styles.markerActive : ''
                            }`}
                            title={t("setSortField")}
                          >
                            <FontAwesomeIcon icon={faSortAmountDown} />
                          </button>
                          <button
                            onClick={() => handleSetListField(field.id)}
                            className={`${styles.markerBtn} ${
                              table.list_field_id === field.id ? styles.markerActive : ''
                            }`}
                            title={t("setListField")}
                          >
                            <FontAwesomeIcon icon={faListUl} />
                          </button>
                        </div>
                      )}
                      
                      {!props.isReportMode && field.name.toLowerCase() !== 'id' && field.file_type !== 'string_disabled' && (
                        <div className={styles.fieldActions}>
                          <button
                            onClick={() => handleEditField(field)}
                            className={styles.editFieldBtn}
                            title={t("edit") || "Edit"}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button
                            onClick={() => handleDeleteField(field)}
                            className={styles.deleteFieldBtn}
                            title={t("delete")}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}

              {showAddField && (
                <div className={styles.addFieldForm}>
                  <input
                    type="text"
                    placeholder={t("fieldName")}
                    value={newFieldData.name}
                    onChange={(e) =>
                      setNewFieldData({ ...newFieldData, name: e.target.value })
                    }
                    className={styles.fieldInput}
                    autoFocus
                  />

                  <span className={styles.fieldTypeFieldName}>
                    {t("fieldNameLatinOnly")}
                  </span>

                  <SearchableDropdown
                    label={t("fieldType")}
                    onOpen={handleDropdownOpen}
                    options={FIELD_TYPES.map(ft => ({
                      label: t(ft.labelKey),
                      value: ft.value,
                    })).filter(item =>
                      item.value.toLowerCase().includes(filter.toLowerCase()) ||
                      item.label.toLowerCase().includes(filter.toLowerCase())
                    )}
                    value={newFieldData.file_type}
                    onSearch={(query: string) => setFilter(query)}
                    onChange={(value) => {
                      setNewFieldData({ ...newFieldData, file_type: value });
                      if (value === "fk") handleRelationDropdownOpen();
                    }}
                  />

                  {(() => {
                    const meta = FIELD_TYPES.find(ft => ft.value === newFieldData.file_type);
                    if (!meta) return null;

                    return (
                      <div className={styles.fieldTypeInfo}>
                        <div className={styles.fieldTypeBLock}>
                          <div className={styles.fieldTypeIcon}>
                            <FontAwesomeIcon icon={meta.icon} />
                          </div>

                          <div className={styles.fieldTypeDesc}>
                            {t(meta.descriptionKey)}
                          </div>
                        </div>

                        {meta.example && (
                          <div className={styles.fieldTypeExample}>
                            <span className={styles.exampleValue}>
                              {t(meta.exampleKey)}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {newFieldData.file_type == 'fk' && (
                    <SearchableDropdown
                      label={t("relationModule")}
                      onOpen={handleRelationDropdownOpen}
                      options={relOptions.filter((item: any) => {
                        return item.label.toString().toLowerCase().indexOf(relationFilter.toLowerCase()) >= 0;
                      })}
                      value={newFieldData.relation_module}
                      onChange={(value) =>
                        setNewFieldData({ ...newFieldData, relation_module: value })
                      }
                      onSearch={(query: string) => {
                        setRelationFilter(query);
                      }}
                    />
                  )}

                  <div className={styles.addFieldButtons}>
                    <button onClick={handleAddField} className={styles.saveFieldBtn}>
                      <FontAwesomeIcon icon={faPlus} />
                      {t("add")}
                    </button>
                    <button
                      onClick={() => {
                        setShowAddField(false);
                        setNewFieldData({ name: "", file_type: "string", relation_module: '' });
                      }}
                      className={styles.cancelFieldBtn}
                    >
                      {t("cancel")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className={styles.noFields}>{t("noFields")}</div>
          )}

          {!showAddField && !props.isReportMode && (
            <button
              onClick={() => setShowAddField(true)}
              className={styles.addFieldButtonBottom}
            >
              <FontAwesomeIcon icon={faPlus} />
              {t("addField")}
            </button>
          )}
        </div>

        <div className={styles.cardFooter}></div>
      </div>

      <DeleteConfirmModal
        isOpen={deletingFieldId !== null}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        fieldName={deletingFieldName}
      />
    </div>
  );
}